import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreditCardOutlined } from '@ant-design/icons';
import moment from 'moment';
import { LiabilityService } from './liability.service';
import { Asset, Liability } from '../../types';
import { useGlobalContext } from '../../store/GlobalContext';
import DetailPage, {
  DetailFields,
  DetailRelated,
  DetailSection,
  DetailStats,
  Field,
  formatCurrency,
} from '../../components/DetailPage';

/** The endpoint returns the secured asset as an object, not just its id. */
interface LiabilityWithAsset extends Omit<Liability, 'asset'> {
  asset?: Asset | string | null;
  label?: string;
  updatedAt?: string;
}

const LiabilityDetails: React.FC = () => {
  const { liabilityId } = useParams<{ liabilityId: string }>();
  const navigate = useNavigate();
  const { selectedEstate } = useGlobalContext();
  const [liability, setLiability] = useState<LiabilityWithAsset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!selectedEstate?.uuid || !liabilityId) {
        setError('No active estate is selected.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setLiability(await LiabilityService.fetchLiabilityById(selectedEstate.uuid, liabilityId));
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load liability');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [liabilityId, selectedEstate?.uuid]);

  /* `asset` is an object when the relation is loaded and a bare id otherwise,
     so only the object form can be linked to. */
  const securedAsset =
    liability?.asset && typeof liability.asset === 'object' ? (liability.asset as Asset) : null;

  const tags = [
    liability?.label ? { label: liability.label, color: 'blue' } : null,
    liability?.source ? { label: liability.source } : null,
  ].filter(Boolean) as { label: string; color?: string }[];

  return (
    <DetailPage
      eyebrow="Liability"
      title={liability?.name || 'Liability'}
      icon={<CreditCardOutlined />}
      tags={tags}
      loading={loading}
      error={error}
      backTo="/liabilities"
      backLabel="Back to liabilities"
    >
      {/* No market-value tile: the asset arrives here as a plain relation, and
          marketValue is computed rather than stored, so it is never populated
          on this route. A tile that can only ever read "not set" is noise. */}
      <DetailStats
        stats={[
          { label: 'Amount owed', value: formatCurrency(liability?.amount) },
          {
            label: 'Secured against',
            value: securedAsset?.name || (liability?.assetName ?? null),
            emptyText: 'Unsecured',
          },
          { label: 'Source', value: liability?.source },
        ]}
      />

      <DetailSection title="Overview">
        <DetailFields>
          <Field label="Description" value={liability?.description} span />
          <Field label="Source" value={liability?.source} />
          <Field label="Label" value={liability?.label} />
          <Field
            label="Added"
            value={liability?.createdAt ? moment(liability.createdAt).format('LL') : null}
          />
          <Field
            label="Last updated"
            value={liability?.updatedAt ? moment(liability.updatedAt).format('LL') : null}
          />
        </DetailFields>
      </DetailSection>

      <DetailSection
        title="Secured asset"
        empty={!securedAsset}
        emptyText="This liability is not secured against an asset"
      >
        <DetailRelated
          items={
            securedAsset
              ? [
                  {
                    key: securedAsset.uid,
                    name: securedAsset.name,
                    meta: [securedAsset.category, securedAsset.location].filter(Boolean).join(' - '),
                    amount: formatCurrency(securedAsset.marketValue),
                    onClick: () => navigate(`/assets/${securedAsset.uid}`),
                  },
                ]
              : []
          }
        />
      </DetailSection>
    </DetailPage>
  );
};

export default LiabilityDetails;
